/**
 * The transport boundary shared by every way of reaching the drawer.
 *
 * WHY THIS EXISTS AS AN INTERFACE. Until now there was exactly one transport
 * (WebUSB) and the rest of the module named its class directly. A second
 * transport makes that a problem: `capability.ts` and `drawer-status.ts` care
 * only about "can it kick, can it read state", and neither should know which
 * bus answered. Both now type against this.
 *
 * WHAT DOES NOT BELONG HERE. No `open()`, no bus-specific handles, and above
 * all no export that reaches the hardware from outside the module. The closed
 * trigger list in `drawer-module.ts` is the security control and it only holds
 * while `kick()` is reachable solely through that file.
 */

export type FailureCode =
  | "unsupported_browser"
  | "permission_lost"
  | "device_disconnected"
  | "kick_timeout";

export class DrawerTransportError extends Error {
  constructor(
    readonly code: FailureCode,
    message: string,
  ) {
    super(message);
    this.name = "DrawerTransportError";
  }
}

/**
 * A paired device, described for a person rather than for a driver.
 *
 * `vendorId`/`productId` are USB facts and Bluetooth has no equivalent, so they
 * are optional. Nothing in the till branches on them — they exist so the
 * settings screen can show which unit was paired.
 */
export interface PeripheralDevice {
  name: string;
  vendorId?: number;
  productId?: number;
}

export type ConnectionState = "connected" | "disconnected" | "permission_lost";

export interface DrawerTransport {
  isSupported(): boolean;
  /** Prompt for a device. REQUIRES A USER GESTURE on every transport. */
  requestPermission(): Promise<PeripheralDevice>;
  connect(): Promise<ConnectionState>;
  isConnected(): boolean;
  /** Whether this device can report drawer state AT ALL (FR-106). */
  hasDrawerStatus(): boolean;
  kick(pin?: 2 | 5): Promise<void>;
  /** `null` means UNKNOWN, never "closed". */
  readDrawerState(): Promise<"open" | "closed" | null>;
  /**
   * Send arbitrary bytes — a ticket, eventually a receipt (FR-108). `kick()`
   * stays a separate method rather than a caller-supplied byte string: the
   * closed drawer-trigger list in drawer-module.ts is the security control,
   * and it only holds while the 5 bytes that open a cash drawer are not
   * reachable through a generic write a ticket module also calls.
   */
  write(bytes: Uint8Array): Promise<void>;
  disconnect(): Promise<void>;
}
